import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { mercadolibreApi } from '../api/mercadolibre'
import Boton from '../components/Boton'
import { colors, font, text, space, radius, sx } from '../design/tokens'
import { fmtFecha, fmtMonto } from '../utils/fecha'
import { descargarArchivo } from '../utils/descargar'
import SelectorArticulosModal from '../components/SelectorArticulosModal'

/**
 * Conexión de LGR con la cuenta de MercadoLibre + prueba de precios por EAN.
 * Solo super admin (Martin, 29 sep 2026: "solo yo"). El Client Secret se
 * guarda cifrado y nunca vuelve a la pantalla.
 *
 * Dos solapas (Martin, 30 sep 2026): la de trabajo (Probar un EAN) abre por
 * defecto y la Conexión queda aparte — el 30 sep un código de barras terminó
 * guardado como Client ID y cortó la conexión. La solapa Conexión se abre sola
 * solo si LGR no está conectado o si se vuelve de autorizar en ML.
 */
export default function MercadolibrePage() {
  const [sp] = useSearchParams()
  const [con, setCon] = useState(null)
  const [clientId, setClientId] = useState('')
  const [secret, setSecret] = useState('')
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState('')
  const [ean, setEan] = useState('')
  const [buscando, setBuscando] = useState(false)
  const [precios, setPrecios] = useState(null)
  const [errorEan, setErrorEan] = useState('')
  const [solapa, setSolapa] = useState(sp.get('ml') ? 'conexion' : 'ean')
  const [bajandoPdf, setBajandoPdf] = useState(false)
  const [errorPdf, setErrorPdf] = useState('')

  const bajarPdf = async () => {
    setBajandoPdf(true); setErrorPdf('')
    try {
      await descargarArchivo(mercadolibreApi.pdfUrl(precios.consulta_id), `precios_ml_${precios.ean}.pdf`)
    } catch {
      setErrorPdf('No se pudo armar el PDF.')
    } finally { setBajandoPdf(false) }
  }

  const cargar = useCallback(async () => {
    const { data } = await mercadolibreApi.conexion()
    setCon(data)
    setClientId(data.client_id || '')
    if (!data.conectado) setSolapa('conexion')
  }, [])
  useEffect(() => { cargar().catch(() => setError('No se pudo leer la conexión.')) }, [cargar])

  const guardar = async (e) => {
    e.preventDefault()
    setGuardando(true); setError('')
    try {
      const { data } = await mercadolibreApi.guardar({ client_id: clientId.trim(), client_secret: secret.trim() || undefined })
      setCon(data); setSecret('')
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo guardar.')
    } finally { setGuardando(false) }
  }

  const autorizar = async () => {
    setError('')
    try {
      const { data } = await mercadolibreApi.autorizar()
      window.location.href = data.url
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo armar la autorización.')
    }
  }

  const [selector, setSelector] = useState(false)

  const consultar = async (codigo) => {
    setBuscando(true); setErrorEan(''); setPrecios(null)
    try {
      const { data } = await mercadolibreApi.precios(codigo)
      setPrecios(data)
    } catch (err) {
      setErrorEan(err.response?.data?.message || 'No se pudo consultar MercadoLibre.')
    } finally { setBuscando(false) }
  }

  const buscar = (e) => { e.preventDefault(); consultar(ean.trim()) }

  // Elegido en el selector único de artículos → se busca por su SKU (el
  // backend usa el EAN del artículo).
  const elegirArticulo = (a) => {
    setEan(a.codigo_alfa)
    consultar(a.codigo_alfa)
  }

  const vuelta = sp.get('ml')

  return (
    <div style={st.page}>
      <h2 style={st.title}>MercadoLibre</h2>
      <p style={st.sub}>Conexión con la cuenta de la empresa para consultar precios del catálogo por EAN. Solo lectura.</p>

      {vuelta === 'ok' && <div style={{ ...st.aviso, ...st.avisoOk }}>✓ LGR quedó conectado a MercadoLibre.</div>}
      {vuelta === 'error' && <div style={{ ...st.aviso, ...st.avisoErr }}>No se pudo conectar: {sp.get('motivo') || 'MercadoLibre no autorizó.'}</div>}

      <div style={st.solapas}>
        {[['ean', 'Buscar precios'], ['conexion', 'Conexión']].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setSolapa(k)}
            style={{ ...st.solapa, ...(solapa === k ? st.solapaOn : null) }}>{label}</button>
        ))}
        {con && (
          <span style={{ marginLeft: 'auto', alignSelf: 'center' }}>
            {con.conectado
              ? <span style={{ ...sx.badge, background: colors.okBg, color: colors.ok }}>Conectado</span>
              : <span style={{ ...sx.badge, background: colors.warnBg, color: colors.warn }}>Sin conectar</span>}
          </span>
        )}
      </div>

      {solapa === 'conexion' && (
      <div style={sx.card}>
        <h3 style={sx.cardTitle}>Conexión</h3>
        <p style={st.muted}>Solo para conectar o reconectar LGR con la app «LGR Ligier» de developers.mercadolibre.com.ar. Para consultar un código de barras usá la solapa «Probar un EAN».</p>
        {!con ? <p style={st.muted}>Cargando…</p> : (
          <>
            <div style={st.estado}>
              {con.conectado
                ? <span style={{ ...sx.badge, background: colors.okBg, color: colors.ok }}>Conectado</span>
                : <span style={{ ...sx.badge, background: colors.warnBg, color: colors.warn }}>Sin conectar</span>}
              {con.cuenta && <span style={st.dato}>Cuenta <strong>{con.cuenta}</strong></span>}
              {con.conectado_at && <span style={st.dato}>desde el <span style={sx.mono}>{fmtFecha(con.conectado_at)}</span></span>}
            </div>
            {con.ultimo_error && <p style={st.errorTxt}>Último error: {con.ultimo_error}</p>}

            <form onSubmit={guardar} style={st.form}>
              <label style={st.label}>
                Client ID
                <input value={clientId} onChange={e => setClientId(e.target.value)} style={{ ...sx.input, ...sx.mono }} inputMode="numeric" autoComplete="off" required />
              </label>
              <label style={st.label}>
                Client Secret {con.tiene_secret && <span style={st.muted}>(guardado · dejalo vacío para no cambiarlo)</span>}
                <input value={secret} onChange={e => setSecret(e.target.value)} type="password" style={sx.input} autoComplete="off" required={!con.tiene_secret} />
              </label>
              <div style={st.acciones}>
                <Boton variante="secondary" type="submit" disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar credenciales'}</Boton>
                <Boton variante="primary" type="button" onClick={autorizar} disabled={!con.client_id || !con.tiene_secret}>
                  {con.conectado ? 'Volver a autorizar' : 'Autorizar en MercadoLibre'}
                </Boton>
              </div>
            </form>
            <p style={st.muted}>Dirección de retorno configurada en la app de ML: <span style={sx.mono}>{con.redirect_uri}</span></p>
          </>
        )}
        {error && <p style={st.errorTxt}>{error}</p>}
      </div>
      )}

      {solapa === 'ean' && (
      <div style={sx.card}>
        <h3 style={sx.cardTitle}>Buscar precios</h3>
        <form onSubmit={buscar} style={st.fila}>
          <input value={ean} onChange={e => setEan(e.target.value)} autoFocus placeholder="EAN o SKU" style={{ ...sx.input, ...sx.mono, maxWidth: 300 }} required />
          <Boton variante="secondary" type="submit" disabled={buscando || !con?.conectado}>{buscando ? 'Buscando…' : 'Buscar en ML'}</Boton>
          <Boton variante="ghost" type="button" disabled={buscando || !con?.conectado} onClick={() => setSelector(true)}>🔍 Elegir artículo</Boton>
          {precios?.consulta_id && (
            <Boton variante="ghost" type="button" style={{ marginLeft: 'auto' }} disabled={bajandoPdf} onClick={bajarPdf}>
              {bajandoPdf ? 'Armando PDF…' : '🖨 Reporte PDF'}
            </Boton>
          )}
        </form>
        <p style={st.muted}>Con un SKU nuestro (tipeado o elegido en 🔍 Elegir artículo), LGR busca en ML con el EAN de ese artículo.</p>
        {errorPdf && <p style={st.errorTxt}>{errorPdf}</p>}
        {errorEan && <p style={st.errorTxt}>{errorEan}</p>}
        {selector && (
          <SelectorArticulosModal
            modo="simple"
            titulo="Elegir artículo para buscar en MercadoLibre"
            soloConEan
            conFoto
            onSelect={elegirArticulo}
            onClose={() => setSelector(false)}
          />
        )}
        {precios && <ResultadoPrecios r={precios} />}
      </div>
      )}
    </div>
  )
}

function ResultadoPrecios({ r }) {
  const lista1 = r.nuestros.find(n => n.lista1 != null)?.lista1 ?? null
  const pos = r.posicion
  return (
    <div style={{ marginTop: space.lg }}>
      <div style={st.bloque}>
        <div style={sx.cardTitle}>
          Nosotros
          <span style={{ ...st.muted, margin: 0, marginLeft: space.md, textTransform: 'none', letterSpacing: 0 }}>
            EAN <span style={sx.mono}>{r.ean}</span>{r.buscado_por_sku ? <> · buscado por SKU <span style={sx.mono}>{r.buscado_por_sku}</span></> : null}
          </span>
        </div>
        {r.nuestros.length === 0 ? <p style={st.muted}>No hay artículos con este EAN en LGR.</p> : r.nuestros.map(n => (
          <div key={n.sku} style={st.renglon}>
            <span style={sx.mono}>{n.sku}</span>
            <span>{n.detalle}{n.anio ? ` · ${n.anio}` : ''}</span>
            <span style={{ marginLeft: 'auto' }}>{n.lista1 != null ? <span style={sx.mono}>$ {fmtMonto(n.lista1)}</span> : <span style={{ ...st.muted, margin: 0 }}>sin LISTA1</span>}</span>
          </div>
        ))}
      </div>

      {(pos || r.comision) && (
        <div style={st.kpis}>
          {pos && (
            <div style={st.kpi}>
              <div style={st.kpiLabel}>Nuestra posición</div>
              <div style={{ ...st.kpiValor, color: pos.dif_pct > 0 ? colors.err : colors.ok }}>{pos.dif_pct > 0 ? '+' : ''}{coma(pos.dif_pct.toFixed(1))} %</div>
              <div style={st.kpiNota}>
                LISTA1 <span style={sx.mono}>$ {fmtMonto(pos.lista1)}</span> vs. la más barata <span style={sx.mono}>$ {fmtMonto(pos.ml_min)}</span>.
                {' '}{pos.mas_baratas} de {pos.ofertas} {pos.ofertas === 1 ? 'oferta está' : 'ofertas están'} por debajo nuestro.
              </div>
            </div>
          )}
          {r.comision && (
            <div style={{ ...st.kpi, flex: 1.4 }}>
              <div style={st.kpiLabel}>Si publicáramos a nuestro LISTA1 (<span style={sx.mono}>$ {fmtMonto(r.comision.precio)}</span>)</div>
              {r.comision.tipos.map(t => (
                <div key={t.id} style={st.comRenglon}>
                  <span style={{ minWidth: 70 }}>{t.nombre}</span>
                  <span style={st.kpiNota}>comisión <span style={sx.mono}>$ {fmtMonto(t.comision)}</span>{t.porcentaje != null ? ` (${coma(t.porcentaje)} %${t.cargo_fijo > 0 ? ` + $ ${fmtMonto(t.cargo_fijo)} fijo` : ''})` : ''}</span>
                  <span style={{ marginLeft: 'auto' }}>nos quedan <b style={sx.mono}>$ {fmtMonto(t.neto)}</b></span>
                </div>
              ))}
              <div style={st.kpiNota}>Sin el envío gratis que ML le cobra al vendedor (depende del peso y el destino).</div>
            </div>
          )}
        </div>
      )}

      {r.productos_ml.length === 0 && <p style={st.muted}>MercadoLibre no tiene este EAN en su catálogo.</p>}
      {r.productos_ml.map(p => (
        <div key={p.id} style={st.bloque}>
          <div style={st.renglon}>
            <a href={p.link} target="_blank" rel="noreferrer" style={st.link}>{p.nombre || p.id}</a>
            <span style={{ ...st.muted, margin: 0, marginLeft: 'auto' }}>
              {p.cantidad_ofertas} {p.cantidad_ofertas === 1 ? 'oferta' : 'ofertas'}{p.ofertas.length < p.cantidad_ofertas ? ` · se muestran las ${p.ofertas.length} más baratas` : ''}
            </span>
          </div>
          {p.error_items && <p style={st.errorTxt}>No se pudieron leer las ofertas ({p.error_items}).</p>}
          {p.ofertas.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={sx.table}>
                <thead><tr>
                  <th style={sx.th}>Publicación</th><th style={sx.th}>Vendedor</th><th style={sx.th}>Zona</th><th style={sx.th}>Envío</th><th style={sx.th}>Tipo</th>
                  <th style={{ ...sx.th, textAlign: 'right' }}>Precio</th><th style={{ ...sx.th, textAlign: 'right' }}>Con envío</th><th style={{ ...sx.th, textAlign: 'right' }}>LISTA1 vs ML</th>
                </tr></thead>
                <tbody>
                  {p.ofertas.map(o => (
                    <tr key={o.item_id} style={st.tr}>
                      <td style={sx.td}>
                        <a href={o.link} target="_blank" rel="noreferrer" style={{ ...st.link, ...sx.mono }}>{o.item_id}</a>
                        {o.tienda_oficial && <div style={st.sec}>Tienda oficial</div>}
                      </td>
                      <td style={sx.td}><Vendedor v={o.vendedor} /></td>
                      <td style={{ ...sx.td, color: colors.textSec }}>{o.zona || '—'}</td>
                      <td style={sx.td}>
                        {o.envio_gratis === true ? 'Gratis' : o.costo_envio != null ? <span style={{ ...sx.mono, whiteSpace: 'nowrap' }}>$ {fmtMonto(o.costo_envio)}</span> : o.envio_gratis === false ? 'Con costo' : '—'}
                        {o.logistica && <div style={st.sec}>{o.logistica}</div>}
                      </td>
                      <td style={sx.td}>{o.tipo_publicacion || '—'}</td>
                      <td style={{ ...sx.td, ...sx.mono, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        $ {fmtMonto(o.precio)}
                        {o.precio_original ? <div style={{ ...st.sec, textDecoration: 'line-through' }}>$ {fmtMonto(o.precio_original)}</div> : null}
                      </td>
                      <td style={{ ...sx.td, ...sx.mono, textAlign: 'right', whiteSpace: 'nowrap' }}>{o.precio_con_envio != null ? `$ ${fmtMonto(o.precio_con_envio)}` : '—'}</td>
                      <td style={{ ...sx.td, ...sx.mono, textAlign: 'right', whiteSpace: 'nowrap', color: lista1 && o.precio ? (lista1 > o.precio ? colors.err : colors.ok) : undefined }}>{lista1 ? difPct(lista1, o.precio) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// Semáforo de reputación de ML (level_id) → color del punto.
const COLOR_REPUTACION = { '5_green': '#16a34a', '4_light_green': '#84cc16', '3_yellow': '#eab308', '2_orange': '#f97316', '1_red': '#dc2626' }

function Vendedor({ v }) {
  if (!v) return <span style={st.sec}>—</span>
  return (
    <div>
      {v.link ? <a href={v.link} target="_blank" rel="noreferrer" style={st.link}>{v.apodo || '—'}</a> : (v.apodo || '—')}
      <div style={st.sec}>
        {v.nivel_id && <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4, background: COLOR_REPUTACION[v.nivel_id] || colors.textMeta, marginRight: 5 }} />}
        {[v.nivel && `Reputación ${v.nivel.toLowerCase()}`, v.mercadolider, v.ventas != null && `${v.ventas.toLocaleString('es-AR')} ventas`].filter(Boolean).join(' · ') || '—'}
      </div>
    </div>
  )
}

// Diferencia de nuestro LISTA1 contra la oferta de ML, con signo: + = estamos más caros.
const difPct = (lista1, precio) => {
  const d = (lista1 / precio - 1) * 100
  return `${d > 0 ? '+' : ''}${coma(d.toFixed(1))} %`
}

const st = {
  page: { padding: space['3xl'], maxWidth: 1200, fontFamily: font.sans },
  title: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  sub: { margin: `${space.xs}px 0 ${space.xl}px`, fontSize: text.body, color: colors.textSec },
  muted: { fontSize: text.sec, color: colors.textMeta, margin: `${space.md}px 0 0` },
  errorTxt: { fontSize: text.sec, color: colors.err, margin: `${space.md}px 0 0` },
  aviso: { padding: `${space.md}px ${space.lg}px`, borderRadius: radius.sm, fontSize: text.body, marginBottom: space.xl },
  avisoOk: { background: colors.okBg, color: colors.ok },
  avisoErr: { background: colors.errBg, color: colors.err },
  estado: { display: 'flex', alignItems: 'center', gap: space.lg, margin: `${space.lg}px 0`, fontSize: text.body, color: colors.textSub },
  dato: { fontSize: text.body, color: colors.textSub },
  form: { display: 'grid', gap: space.lg, maxWidth: 480 },
  label: { display: 'grid', gap: space.xs, fontSize: text.sec, color: colors.textSec },
  acciones: { display: 'flex', gap: space.md },
  fila: { display: 'flex', gap: space.md, marginTop: space.lg },
  bloque: { marginTop: space.lg, paddingTop: space.lg, borderTop: `1px solid ${colors.borderSoft}` },
  renglon: { display: 'flex', gap: space.lg, alignItems: 'baseline', fontSize: text.body, color: colors.text, padding: `${space.xs}px 0` },
  link: { color: colors.link, textDecoration: 'none' },
  solapas: { display: 'flex', gap: space.xl, marginBottom: space.xl, borderBottom: `1px solid ${colors.border}` },
  solapa: { padding: '6px 2px', marginBottom: -1, background: 'none', cursor: 'pointer', border: 'none', borderBottom: '2px solid transparent', color: colors.textSec, fontSize: text.body },
  solapaOn: { borderBottom: `2px solid ${colors.link}`, color: colors.link, fontWeight: 600 },
  tr: { height: 44, borderBottom: `1px solid ${colors.borderSoft}` },
  sec: { fontSize: text.meta, color: colors.textSec, marginTop: 2 },
  kpis: { display: 'flex', gap: space.lg, marginTop: space.lg, flexWrap: 'wrap' },
  kpi: { flex: 1, minWidth: 240, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: `${space.md}px ${space.lg}px` },
  kpiLabel: { fontSize: text.meta, textTransform: 'uppercase', letterSpacing: '0.05em', color: colors.textSec, fontWeight: 600 },
  kpiValor: { fontSize: 22, fontWeight: 700, fontFamily: font.mono, margin: `${space.xs}px 0` },
  kpiNota: { fontSize: text.sec, color: colors.textSec },
  comRenglon: { display: 'flex', gap: space.md, alignItems: 'baseline', fontSize: text.body, padding: `${space.xs}px 0`, borderBottom: `1px solid ${colors.borderSoft}` },
}

// Decimales con coma, como el resto de los importes de la pantalla.
const coma = (v) => String(v).replace('.', ',')
